import React, { useCallback, useEffect, useState } from 'react';
import { notification, Tag } from 'antd';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import { IEtapaSolicitacaoAtivacao, ISolicitacaoAtivacaoListItem } from './interfaces';

import { useGetColumnSearchPropsData } from '../../../hooks/FilterTable/date';
import { useGetColumnSearchPropsObj } from '../../../hooks/FilterTable/objString';
import SolicitacoesDeAtivacaoService from '../../../services/http/services/SaldoDeContas/SolicitacoesDeAtivacaoService';

export const useSolicitacoesDeAtivacao = () => {
    const refContagemDoFiltro = React.useRef<Array<any>>([]);

    const history = useHistory();

    const signal = axios.CancelToken.source();

    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([]);
    const [dadosTabelaState, setDadosTabelaState] = useState<ISolicitacaoAtivacaoListItem[]>([]);
    const [hasSelectedEdit, setHasSelectedEdit] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);

    const loadData = useCallback(() => {

        setLoadingTable(true);

        const loadSolicitacoes = async () => {
            try {
                const data = await SolicitacoesDeAtivacaoService.getSolicitacoesAtivacao(signal.token);

                setDadosTabelaState(data);
                setLoadingTable(false)

            } catch (error) {
                let msgErro: any = (error as Error);
                if (msgErro?.message != 'Requisicao cancelada!') {
                    setLoadingTable(false)
                    notification.error({
                        message: 'Erro',
                        description:
                            'Não foi possivel buscar os dados!',
                    });
                }
            }
        }

        loadSolicitacoes();

    }, []);

    useEffect(() => {
        loadData();

        return () => {
            signal.cancel();
        }
    }, []);

    const clickBotaoEditarUsuario = useCallback(() => {
        if (selectedRowKeys.length == 1) {
            history.push(`/solicitacoes-de-ativacao/gerenciar/${selectedRowKeys[0]}`);
        }
    }, [selectedRowKeys, dadosTabelaState]);

    const onclickTable = useCallback((record: ISolicitacaoAtivacaoListItem, row: any): void => {

        if (selectedRowKeys.includes(record.id)) {
            let filter = selectedRowKeys.filter((val) => {
                return val != record.id
            })
            if (filter.length === 1) {
                setSelectedRowKeys(filter);
                setHasSelectedEdit(true);
            } else if (filter.length === 0) {
                setSelectedRowKeys(filter);
                setHasSelectedEdit(false);
            } else {
                setSelectedRowKeys(filter);
            }

        } else {
            if (selectedRowKeys.length == 0) {
                setSelectedRowKeys([record.id]);
                setHasSelectedEdit(true);
            } else {
                setSelectedRowKeys([record.id]);
                setHasSelectedEdit(true);
            }
        }
    }, [selectedRowKeys]);

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setHasSelectedEdit(false);
    }, []);

    const onSelectChange = useCallback((selectedRowKeys): void => {
        if (selectedRowKeys.length > 1) {
            setSelectedRowKeys([selectedRowKeys[1]]);
            setHasSelectedEdit(true);
        } else if (selectedRowKeys.length === 0) {
            setSelectedRowKeys(selectedRowKeys);
            setHasSelectedEdit(false);
        } else {
            setSelectedRowKeys([selectedRowKeys[0]]);
            setHasSelectedEdit(true);
        }
    }, [selectedRowKeys]);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: '1%',
        type: 'radio' as any
    };

    const columns = [
        {
            title: 'Contrato',
            dataIndex: 'contrato',
            ...useGetColumnSearchPropsObj('contrato'),
            width: '4%',
            render: (contrato: any) => (
                contrato?.nome
            ),
        },
        {
            title: 'Etapa',
            dataIndex: 'etapaAtual',
            width: '2.5%',
            filters: [
                {
                    text: '1 - Integração com sistema contábil',
                    value: 1,
                },
                {
                    text: '2 - Alinhamento de fontes e contas',
                    value: 2,
                },
                {
                    text: '3 - Integração com bancos',
                    value: 3,
                },
                {
                    text: '4 - Definição das permissões dos usuários',
                    value: 4,
                },
                {
                    text: 'Concluída',
                    value: 'Concluída',
                },
            ],
            onFilter: (value: any, record: ISolicitacaoAtivacaoListItem) => {
                if (value === "Concluída") {
                    return !record.etapaAtual && !!record.concluidaEm;
                }

                return record.etapaAtual?.etapa.ordem === value;
            },
            render: (val: IEtapaSolicitacaoAtivacao | null, record: ISolicitacaoAtivacaoListItem) => {
                let cor = '';
                switch (val?.etapa.nome) {
                    case 'Integração com sistema contábil':
                        cor = '#7CBC3B';
                        break;
                    case "Alinhamento de fontes e contas":
                        cor = '#DDCA00';
                        break;
                    case "Integração com bancos":
                        cor = '#167DDD';
                        break;
                    case "Definição das permissões dos usuários":
                        cor = '#933BBC';
                        break;
                    default:
                        cor = ''
                        break;
                }

                const concluida = !val && record.concluidaEm;

                return (
                    <span>
                        {
                            concluida ? (
                                <Tag color={"#17751b"}>
                                    <span style={{ fontWeight: 600 }}>
                                        Concluída
                                    </span>
                                </Tag>
                            ) : val?.etapa.nome ? (
                                <Tag color={cor} key={val.id}>
                                    <span style={{ fontWeight: 600 }} >
                                        {val.etapa.ordem} - {val.etapa.nome}
                                    </span>
                                </Tag>
                            ) : null
                        }
                    </span>
                )
            },
        },
        {
            title: 'Sistema contábil',
            dataIndex: 'sistemaContabil',
            ...useGetColumnSearchPropsObj('sistemaContabil', "nome", undefined, "sistemaContabilOutro"),
            width: '4%',
            render: (sistemaContabil: any, data: ISolicitacaoAtivacaoListItem) => (
                sistemaContabil?.nome === "Outro" ? `Outro - ${data.sistemaContabilOutro}` : sistemaContabil.nome
            ),
        },
        {
            title: 'Empenhos e restos',
            dataIndex: 'empenhosERestos',
            width: '4%',
            filters: [
                {
                    text: 'Sim',
                    value: true,
                },
                {
                    text: 'Não',
                    value: false,
                },
            ],
            onFilter: (value: any, record: ISolicitacaoAtivacaoListItem) => {
                return value ? record.empenhosERestos : !record.empenhosERestos;
            },
            render: (empenhosERestos: boolean) => empenhosERestos ? "Sim" : "Não"
        },
        {
            title: 'Possui contas inativas',
            dataIndex: 'possuiContasInativas',
            width: '4%',
            filters: [
                {
                    text: 'Sim',
                    value: true,
                },
                {
                    text: 'Não',
                    value: false,
                },
            ],
            onFilter: (value: any, record: ISolicitacaoAtivacaoListItem) => {
                return value ? record.possuiContasInativas : !record.possuiContasInativas;
            },
            render: (possuiContasInativas: boolean) => possuiContasInativas ? "Sim" : "Não"
        },
        {
            title: 'Data solicitação',
            dataIndex: 'createdAt',
            ...useGetColumnSearchPropsData('createdAt'),
            width: '2%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.createdAt) {
                    aMonMen = moment(a.createdAt).unix()
                } else {
                    aMonMen = 0
                }
                if (b.createdAt) {
                    bMonMen = moment(b.createdAt).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (criado: any) => (
                moment(criado).format('DD/MM/YYYY HH:mm')
            ),
        },
        {
            title: 'Data conclusão',
            dataIndex: 'concluidaEm',
            ...useGetColumnSearchPropsData('concluidaEm'),
            width: '2%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.concluidaEm) {
                    aMonMen = moment(a.concluidaEm).unix()
                } else {
                    aMonMen = 0
                }
                if (b.concluidaEm) {
                    bMonMen = moment(b.concluidaEm).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (concluidaEm: any) => (
                concluidaEm ? moment(concluidaEm).format('DD/MM/YYYY HH:mm') : null
            ),
        },
    ];

    return {
        clickBotaoEditarUsuario,
        hasSelectedEdit,
        loadingTable,
        onclickTable,
        rowSelection,
        dadosTabelaState,
        columns,
        refContagemDoFiltro,
        handleResetRowSelection,
    }
}
